import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { enrollmentService, EnrollmentWithProduct } from '@/services/enrollment.service';
import { trackDashboard, trackPageView } from '@/services/analytics';
import { useScrollTracking } from '@/hooks/useAnalytics';

const DashboardCourses: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((state: any) => state.user.user);
  const [enrollments, setEnrollments] = useState<EnrollmentWithProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track scroll depth and page view
  useScrollTracking('DashboardCourses');

  useEffect(() => {
    trackDashboard.view('courses');
    trackPageView('DashboardCourses');
  }, []);

  useEffect(() => {
    const fetchEnrollments = async () => {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        navigate('/signin');
        return;
      }

      try {
        setLoading(true);
        const response = await enrollmentService.getMyEnrollments();

        if (response.success && response.data) {
          // Filter to only show COURSE enrollments (exclude bundles)
          const courseEnrollments = response.data.filter(
            (enrollment) => enrollment.product_type === 'course'
          );
          setEnrollments(courseEnrollments);
        } else {
          setError(response.error || 'Failed to load your courses');
        }
      } catch (err) {
        console.error('Error fetching enrollments:', err);
        setError('An error occurred while loading your courses');
      } finally {
        setLoading(false);
      }
    };

    fetchEnrollments();
  }, [navigate]);

  const handleCourseClick = (enrollment: EnrollmentWithProduct) => {
    // Track course resume
    trackDashboard.courseResume(enrollment.product_id || '', enrollment.product_title || '');

    // Navigate to course classroom page
    const courseId = enrollment.product_id;
    if (!courseId) {
      console.error('No course ID found for enrollment:', enrollment);
      setError('Unable to access this course. Please contact support.');
      return;
    }
    navigate(`/classroom/course/${courseId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getProgressColor = (progress: number) => {
    if (progress === 0) return 'bg-gray-500';
    if (progress < 50) return 'bg-yellow-500';
    if (progress < 100) return 'bg-blue-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg">Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            My Courses
          </h1>
          <p className="text-gray-300 text-lg">
            Continue your learning journey
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        {/* Enrollments Grid */}
        {enrollments.length === 0 ? (
          <div className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-2xl p-12 text-center">
            <svg 
              className="w-24 h-24 mx-auto mb-4 text-gray-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
              />
            </svg>
            <h3 className="text-2xl font-semibold text-gray-300 mb-2">No courses yet</h3>
            <p className="text-gray-400 mb-6">Start your learning journey by enrolling in a course</p>
            <button
              onClick={() => navigate('/courses')}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg hover:shadow-indigo-500/50 transition-all"
            >
              Browse Courses
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enrollments.map((enrollment) => (
              <div
                key={enrollment.id}
                className="bg-slate-800/60 backdrop-blur-sm border-2 border-indigo-500/30 rounded-xl overflow-hidden hover:border-indigo-500 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-500/30 cursor-pointer"
                onClick={() => handleCourseClick(enrollment)}
              >
                {/* Thumbnail */}
                <div className="h-48 bg-gradient-to-br from-indigo-600 to-purple-600 relative overflow-hidden">
                  {enrollment.product_details?.thumbnail_url ? (
                    <img 
                      src={enrollment.product_details.thumbnail_url} 
                      alt={enrollment.product_title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg 
                        className="w-20 h-20 text-white/50" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
                        />
                      </svg>
                    </div>
                  )}
                  
                  {/* Certificate Badge */}
                  {enrollment.certificate_issued && (
                    <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Certificate
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
                    {enrollment.product_title}
                  </h3>
                  
                  {enrollment.product_details?.description && (
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {enrollment.product_details.description}
                    </p>
                  )}

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-400">Progress</span>
                      <span className="text-xs font-semibold text-gray-300">
                        {enrollment.progress_percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(enrollment.progress_percentage)}`}
                        style={{ width: `${enrollment.progress_percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Footer Info */}
                  <div className="flex justify-between items-center text-xs text-gray-500 pt-4 border-t border-gray-700">
                    <span>Enrolled {formatDate(enrollment.enrolled_at)}</span>
                    <span className="capitalize">{enrollment.product_type}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default DashboardCourses;
